import React from 'react';
import { connect } from 'react-redux';
import styled, { ThemeProvider } from 'styled-components';

import Layout from '../../layout';
import lightTheme from '../../themes/light';
import withSentry from '../../utils/withSentry';
import withi18n from '../../utils/withI18n';
import { getLabel } from '../../utils/i18n';
import { gwGet } from '../../actions/actionsFactory/gwRequest';
import { checkMobile } from '../../utils/redirectToMobileSite.mjs';

import GetBaseInitialProps from '../../network/GetBaseInitialProps';
import metaInfo from '../../config/metaInfo';
import activityData from './data';
import ErrorPage from '../_error';
import Container from '../../components/Container';
import FlexBox from '../../components/FlexBox';
import { Link } from '../../routes';
import RankingTable from './RankingTable';

const BannerBox = styled.div`
  width: 100%;
  height: ${({ height }) => `${height}px`};
  background-image: url(${({ bg }) => bg});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  position: relative;
  > .activityTime {
    position: absolute;
    width: 100%;
    left: 0;
    bottom: 30px;
    font-weight: 500;
    font-size: 24px;
    line-height: 31px;
    color: #fff;
    text-align: center;
  }
  @media screen and (max-width: 414px) {
    .activityTime {
      font-size: 12px;
      line-height: 100%;
      top: 0;
    }
  }
`;

const Desc = styled.div`
  background: #fff;
  font-size: 18px;
  line-height: 1.5;
  color: #48515d;
  padding: 40px 20px;
  text-align: center;
  font-family: ${({ theme }) => theme.fontIBMRegular};
  > strong {
    font-family: ${({ theme }) => theme.fontIBMSemiBold};
  }
  time {
    font-weight: 500;
    font-size: 18px;
    line-height: 23px;
    color: #212833;
    display: block;
    margin-bottom: 8px;
    font-family: ${({ theme }) => theme.fontIBMSemiBold};
  }
  @media screen and (max-width: 414px) {
    padding: 20px;
    text-align: left;
    font-size: 14px;
    line-height: 18px;
  }
`;

const Section = styled(FlexBox)`
  width: 100%;
  padding: ${({ padding }) => padding || '80px 10px'};
  background-color: ${({ bg }) => bg};
  @media screen and (max-width: 414px) {
    padding: 30px 10px;
  }
`;

const SectionTitle = styled.div`
  font-family: ${({ theme }) => theme.fontIBMLight};
  font-size: 42px;
  color: #212833;
  line-height: 1.35;
  font-weight: normal;
  margin-bottom: 85px;
  text-align: center;
  position: relative;
  :after {
    content: ' ';
    display: inline-block;
    width: 60px;
    height: 5px;
    background: #f0b90b;
    position: absolute;
    left: 50%;
    margin-left: -30px;
    bottom: -20px;
  }

  @media screen and (max-width: 414px) {
    font-size: 24px;
    line-height: 31px;
    margin-bottom: 47px;
  }
`;
const SectionDesc = styled.div`
  font-family: ${({ theme }) => theme.fontIBMRegular};
  font-size: 16px;
  line-height: 22px;
  color: #48515d;
  margin-bottom: 30px;
  a,
  a:visited {
    text-decoration: underline;
  }
  a:hover {
    color: ${({ theme }) => theme.highlight};
  }
  strong {
    font-family: ${({ theme }) => theme.fontIBMBold};
    font-size: 18px;
  }
  @media screen and (max-width: 414px) {
    font-size: 14px;
    line-height: 18px;
  }
`;

const RewardsBox = styled(FlexBox)`
  width: 100%;
  background: #ffffff;
  box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  @media screen and (max-width: 414px) {
    background: none;
    box-shadow: none;
  }
`;

const RewardItem = styled(FlexBox)`
  padding: 30px;
  border-right: 1px solid #eaecef;
  background: #ffffff;
  font-family: ${({ theme }) => theme.fontIBMMedium};
  h4 {
    font-size: 26px;
    line-height: 33px;
    margin: 0 0 12px;
    color: #212833;
  }

  p {
    font-size: 16px;
    line-height: 21px;
    color: #848e9c;
    height: 106px;
    margin-bottom: 40px;
  }
  :last-of-type {
    border-right: none;
  }
  @media screen and (max-width: 414px) {
    padding: 20px;
    border-right: none;
    flex: auto;
    margin-bottom: 10px;
    flex-wrap: nowrap;
    box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);
    border-radius: 3px;
    justify-content: space-between;
    h4 {
      font-size: 18px;
      line-height: 23px;
      margin: 0;
    }
    p {
      font-size: 14px;
      line-height: 18px;
      height: auto;
      margin: 0;
    }
    img {
      width: 66px;
    }
  }
`;

const ActivityItems = styled.div`
  font-family: ${({ theme }) => theme.fontIBMRegular};
  h3 {
    font-size: 16px;
    line-height: 22px;
    font-weight: 500;
    color: #48515d;
  }
  ul {
    list-style-type: none;
    margin-bottom: 30px;
  }
  li {
    font-size: 14px;
    line-height: 22px;
    color: #848e9c;
    font-family: ${({ theme }) => theme.fontIBMLight};
  }
  p.riskTip {
    font-size: 14px;
    line-height: 22px;
    color: #848e9c;
    font-family: ${({ theme }) => theme.fontIBMLight};
  }
`;

const TradeRanks = styled.div`
  width: 100%;
  background: #ffffff;
  box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.05);
  border-radius: 3px;
  padding: 40px 150px;
  @media screen and (max-width: 414px) {
    padding: 24px 10px 30px;
  }
`;

const RankTitle = styled(FlexBox)`
  font-family: ${({ theme }) => theme.fontIBMLight};
  font-size: 54px;
  line-height: 70px;
  padding: 0 30px;
  position: relative;
  :after,
  :before {
    content: ' ';
    display: inline-block;
    width: 22px;
    height: 22px;
    background: linear-gradient(315deg, #f0b90b 0.5%, #fbda3c 98.51%);
    transform: rotate(45deg) translateY(-50%);
    position: absolute;
    top: 50%;
  }
  :after {
    right: -18px;
  }
  :before {
    left: -34px;
  }
  @media screen and (max-width: 414px) {
    font-size: 24px;
    line-height: 31px;
    :after,
    :before {
      width: 10px;
      height: 10px;
    }
    :after {
      right: -2px;
    }
    :before {
      left: -9px;
    }
  }
`;

const UserRankInfo = styled.div`
  width: 100%;
  text-align: center;
  font-size: 16px;
  line-height: 21px;
  color: #212833;
  margin: 30px 0;
  a,
  a:visited {
    color: ${({ theme }) => theme.highlight};
  }
  a:hover {
    text-decoration: underline;
  }
  @media screen and (max-width: 414px) {
    font-weight: 500;
    font-size: 13px;
    line-height: 17px;
    margin: 14px 0 20px;
  }
`;

const UpdataTime = styled.p`
  text-align: center;
`;

const { brands } = metaInfo;

type Props = {
  metaData?: Object,
  url?: Object,
  i18n?: Object,
  locale?: string,
  activityId: string,
  isLoggedIn: boolean,
  tradeAmountList: Object,
};

type State = {
  tradeAmountList: Object,
  clientType: string,
};

const mapStateToProps = ({ isLoggedIn }) => ({ isLoggedIn });

export default
@withSentry
@withi18n
@connect(mapStateToProps)
class ActivityWin extends React.Component<Props, State> {
  static defaultProps = {
    metaData: {},
    url: {},
    i18n: {},
    locale: 'en',
    asset: '',
  };

  static getInitialProps = async ctx => {
    const initProps = await GetBaseInitialProps(ctx, { withProductsData: false });
    const { id: activityId } = ctx.query;
    return {
      ...initProps,
      activityId,
    };
  };

  state = {
    tradeAmountList: {},
    clientType: 'pc',
  };

  async componentDidMount() {
    const { activityId } = this.props;
    const result = await gwGet(`/activity/getTradeAmountUserList.html?activityId=${activityId}`);
    this.setState({
      tradeAmountList: result,
      clientType: checkMobile(false, null) ? 'mobile' : 'pc',
    });
  }

  _getData = () => {
    const { locale, activityId } = this.props;
    const lang = locale === 'cn' ? 'cn' : 'en';
    const result = activityData.filter(({ id }) => activityId === id)[0];
    if (!result) return null;
    return result[lang];
  };

  _getOrderIcon = orderNo => {
    if (orderNo === '1') return <img width="104" src={require('./images/rankingNo_1.svg')} alt={`NO${orderNo}`} />;
    if (orderNo === '2') return <img width="104" src={require('./images/rankingNo_2.svg')} alt={`NO${orderNo}`} />;
    if (orderNo === '3') return <img width="104" src={require('./images/rankingNo_3.svg')} alt={`NO${orderNo}`} />;
    return <img width="104" src={require('./images/gift.svg')} alt={`NO${orderNo}`} />;
  };

  render() {
    const {
      url, locale, metaData, i18n, isLoggedIn, activityId,
    } = this.props;
    const { tradeAmountList, clientType } = this.state;
    const data = this._getData();
    if (!data) return <ErrorPage i18n={i18n} locale={locale} statusCode={404} />;
    const loginCallbackPath = `/${locale}/market-activity/${activityId}`;
    return (
      <ThemeProvider theme={lightTheme}>
        <Layout
          url={url}
          locale={locale}
          metaData={{
            ...metaData,
            title: `${data.pageTitle} | ${brands[locale]}`,
            description: data.pageDescription,
          }}
          isGreyBg={false}
          omitFooterTotalUpdate={true}
          isMobile={clientType === 'mobile'}
          omitFooter={clientType === 'mobile'}
        >
          <BannerBox
            height={data.banner[clientType].height}
            bg={require(`${data.banner[clientType].path}`)}
          />
          <Desc>
            <Container>
              {data.time && <time>{data.time}</time>}
              {data.description}
            </Container>
          </Desc>
          <Section bg="#fff" justify="center" padding="0 10px 40px">
            <Container>
              <TradeRanks>
                {data.isTradingRank && (
                  <FlexBox justify="center">
                    <RankTitle>{getLabel('exchange-activity-tradingRankList', i18n)}</RankTitle>
                  </FlexBox>
                )}
                {isLoggedIn ? (
                  <UserRankInfo>
                    {getLabel('exchange-activity-yourTradingVolumn', i18n)}:{' '}
                    <strong>
                      {tradeAmountList.userTradeAmount || 0} {data.name}
                    </strong>
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    {data.isTradingRank && (
                      <>
                        {getLabel('exchange-activity-yourRanking', i18n)}:&nbsp;
                        <strong>
                          {tradeAmountList.userOrderNo > 50000 || !tradeAmountList.userOrderNo
                            ? getLabel('exchange-activity-outOf50000', i18n)
                            : tradeAmountList.userOrderNo}
                        </strong>
                        &nbsp;&nbsp;&nbsp;&nbsp;
                      </>
                    )}
                    {clientType === 'pc' && (
                      <Link route={`/${locale}/trade/${data.symbol}`}>
                        <a>{getLabel('exchange-activity-goToTrade', i18n)}»</a>
                      </Link>
                    )}
                    {clientType === 'mobile' && (
                      <a href={`/indexSpa.html#/trade/trade?symbol=${data.symbol}`}>
                        {getLabel('exchange-activity-goToTrade', i18n)}&nbsp;»
                      </a>
                    )}
                  </UserRankInfo>
                ) : (
                  <UserRankInfo>
                    {getLabel('exchange-activity-yourTradingVolumn', i18n)}:{' '}
                    <strong>? {data.name}</strong>&nbsp;&nbsp;&nbsp;&nbsp;
                    {data.isTradingRank && (
                      <>
                        {getLabel('exchange-activity-yourRanking', i18n)}: <strong>?</strong>
                        &nbsp;&nbsp;&nbsp;&nbsp;
                      </>
                    )}
                    {getLabel('exchange-activity-pleaseFirst', i18n)}&nbsp;
                    {clientType === 'pc' && (
                      <Link route={`/${locale}/login?callback=${loginCallbackPath}`}>
                        <a>{getLabel('exchange-login-signIn', i18n)}&nbsp;»</a>
                      </Link>
                    )}
                    {clientType === 'mobile' && (
                      <a href={`/m-login.html?callback=${loginCallbackPath}`}>
                        {getLabel('exchange-login-signIn', i18n)}&nbsp;»
                      </a>
                    )}
                  </UserRankInfo>
                )}
                {data.updataTime && <UpdataTime>{data.updataTime}</UpdataTime>}
                {data.isTradingRank && (
                  <RankingTable i18n={i18n} data={tradeAmountList.data} asset={data.name} />
                )}
              </TradeRanks>
            </Container>
          </Section>
          {data.partA && (
            <Section bg="rgba(234,236,239,0.3)">
              <Container>
                <SectionTitle>{data.partA.title}</SectionTitle>
                <SectionDesc>{data.partA.desc}</SectionDesc>
                <RewardsBox>
                  {data.partA.rewards.map(({ orderNo, title, desc }, index) => (
                    <RewardItem flex="1" key={index}>
                      <div>
                        <h4>{title}</h4>
                        <p>{desc}</p>
                      </div>
                      {this._getOrderIcon(orderNo)}
                    </RewardItem>
                  ))}
                </RewardsBox>
              </Container>
            </Section>
          )}
          {data.partB && (
            <Section bg="#fff">
              <Container>
                <SectionTitle>{data.partB.title}</SectionTitle>
                <SectionDesc>{data.partB.desc}</SectionDesc>
                <RewardsBox>
                  {data.partB.rewards.map(({ orderNo, title, desc }, index) => (
                    <RewardItem flex="1" key={index}>
                      <div>
                        <h4>{title}</h4>
                        <p>{desc}</p>
                      </div>
                      {this._getOrderIcon(orderNo)}
                    </RewardItem>
                  ))}
                </RewardsBox>
              </Container>
            </Section>
          )}
          {data.partC && (
            <Section bg="#fff">
              <Container>
                <SectionTitle>{data.partC.title}</SectionTitle>
                <SectionDesc>{data.partC.desc}</SectionDesc>
                <RewardsBox>
                  {data.partC.rewards.map(({ orderNo, title, desc }, index) => (
                    <RewardItem flex="1" key={index}>
                      <div>
                        <h4>{title}</h4>
                        <p>{desc}</p>
                      </div>
                      {this._getOrderIcon(orderNo)}
                    </RewardItem>
                  ))}
                </RewardsBox>
              </Container>
            </Section>
          )}
          <Section bg="rgba(234,236,239,0.3)">
            <Container>
              <ActivityItems>
                <h3>{getLabel('exchange-common-terms', i18n)}: </h3>
                <ul>
                  {data.terms.map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
                <h3>{getLabel('exchange-common-riskWarning', i18n)}: </h3>
                <p className="riskTip">{data.riskTip}</p>
              </ActivityItems>
            </Container>
          </Section>
        </Layout>
      </ThemeProvider>
    );
  }
}
